import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { HiMenu, HiX } from 'react-icons/hi';
import { BsChevronDown } from "react-icons/bs";

import { usePostgresUser } from '../components/utils/PostgresUserContext';

import logoImage from '../images/landingPageAssets/onikanji-logo.png';
import mikanChanFloatingMainGraphic from '../images/landingPageAssets/onikanji-mikan-chan-hero-banner.webp';

// Selling Points

// What You Will Learn Icons
import joyoKanjiIcon from '../images/landingPageAssets/joyo-kanji-completion.webp';
import bookIcon from '../images/landingPageAssets/book-icon.png';
import mangaIcon from '../images/landingPageAssets/manga-icon.png';
import nihonIcon from '../images/landingPageAssets/nihon-icon.png';
import backgroundTexture from '../images/landingPageAssets/background-texture.webp';
import newLessonsImage from '../images/landingPageAssets/onikanji-landing-page-new-lesson-kanji-overview.webp';
import srsReviewImage1 from '../images/landingPageAssets/onikanji-quiz-engine-1.png';
import srsReviewImage2 from '../images/landingPageAssets/onikanji-quiz-engine-2.png';
import statsTrackingImage from '../images/landingPageAssets/onikanji-tracks-stats.webp';
import myStatsImage from '../images/landingPageAssets/onikanji-landing-page-my-stats.webp';
import leaderboardImage from '../images/landingPageAssets/onikanji-landing-page-leaderboard-2026-10.jpg';
import learnGraphicImage from '../images/landingPageAssets/onikanji-learn-graphic.webp';

// Upload Your Own Manga
import uploadYourMangaImage from '../images/landingPageAssets/onikanji-landing-page-upload-your-manga.webp';

// Testimonial Video
import craneGameVideo from '../images/landingPageAssets/onikanji-landing-page-crane-game-asset.mp4';

// FAQ Image
import faqImage from '../images/landingPageAssets/onikanji-landing-page-faqs.webp';

import googlePlayBadge from '../images/landingPageAssets/google-play-badge.png';
import androidDashboardPhone from '../images/landingPageAssets/onikanji-android-dashboard-phone.webp';
import Footer from '../components/footer';
import OutputBattleTeaser from '../components/OutputBattleTeaser';

import levelUpImage from '../images/landingPageAssets/onikanji-landing-page-level-up.webp';
import godzillaJoinUsDesktopImage from '../images/landingPageAssets/onikanji-godzilla-join-us-desktop.webp';
import godzillaJoinUsMobileImage from '../images/landingPageAssets/onikanji-godzilla-join-us-mobile.webp';

// The Android app (a TWA of this site). The referrer shows up in Play Console
// acquisition reports, by where on the page the badge was tapped.
const playStoreUrl = (placement) =>
    `https://play.google.com/store/apps/details?id=com.onikanji.app&referrer=${encodeURIComponent(`utm_source=onikanji_landing&utm_medium=website&utm_content=${placement}`)}`;

const OniKanjiLandingPage = () => {

    const godzillaJoinUsImage = godzillaJoinUsDesktopImage;
    const godzillaJoinUsImageMobile = godzillaJoinUsMobileImage;
    
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeNewLessonsImage, setActiveNewLessonsImage] = useState(1);
    const [activeSRSReviewsImage, setActiveSRSReviewsImage] = useState(1);
    const [activeLevelUpImage, setActiveLevelUpImage] = useState(1);
    const menuRef = useRef(null);
    const { user, loading, subscriptionStatus } = usePostgresUser();
    const location = useLocation();
    
    // SEO metadata
    const pageTitle = "OniKanji - Master Japanese Kanji with SRS Flashcards & Immersion";
    const pageDescription = "Master Japanese kanji with OniKanji's spaced repetition flashcards and context-based lessons, then read the manga you own with automatic furigana, one-tap translations, and grammar breakdowns.";
    const pageKeywords = "learn kanji, Japanese kanji, SRS flashcards, spaced repetition, kanji app, learn Japanese, JLPT study, kanji quiz, context-based learning, manga reader, read manga in Japanese, kanji meanings, kanji readings, Japanese vocabulary, gamified learning";
    const pageImage = "https://onikanji.com/og-image.png";

    const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
    const [openFaqIndex, setOpenFaqIndex] = useState(null);

    useEffect(() => {
        document.title = pageTitle;
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsLargeScreen(window.innerWidth >= 1024);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const toggleFaq = (index) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    return (
        <div className="font-noto-sans-jp bg-white relative">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={pageKeywords} />
                
                {/* Open Graph Tags for social sharing */}
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:image" content={pageImage} />
                <meta property="og:url" content="https://onikanji.com" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="OniKanji" />
                <meta property="og:locale" content="en_US" />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content={pageImage} />

                {/* Canonical URL */}
                <link rel="canonical" href="https://onikanji.com" />
                
                {/* Structured data - WebApplication */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebApplication",
                        "name": "OniKanji",
                        "url": "https://onikanji.com",
                        "description": pageDescription,
                        "applicationCategory": "EducationalApplication",
                        "offers": {
                            "@type": "Offer",
                            "price": "9.00",
                            "priceCurrency": "USD",
                            "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]
                        },
                        "operatingSystem": "Web browser",
                        "screenshot": pageImage
                    })}
                </script>

                {/* Structured data - EducationalOrganization */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "EducationalOrganization",
                        "name": "OniKanji",
                        "url": "https://onikanji.com",
                        "logo": "https://onikanji.com/onikanji-mikan-chan-learn-kanji.png",
                        "sameAs": [
                            "https://bsky.app/profile/onikanji.bsky.social",
                            "https://www.tiktok.com/@onikanji6",
                            "https://discord.gg/7TEG9cqZTP"
                        ],
                        "description": "OniKanji helps learners master Japanese kanji through spaced repetition flashcards, context-based lessons, and immersion reading with manga, light novels, and podcasts.",
                        "areaServed": "Worldwide",
                        "teaches": [
                            "Japanese Kanji",
                            "Japanese Vocabulary",
                            "Japanese Reading Comprehension"
                        ]
                    })}
                </script>

                {/* Structured data - FAQPage (mirrors the on-page FAQ section) */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            { "@type": "Question", "name": "What makes OniKanji different from other Japanese learning apps?", "acceptedAnswer": { "@type": "Answer", "text": "OniKanji focuses on a context-first approach to learning kanji. Each review question is paired with a relevant context sentence that serves as a natural mnemonic to help you remember the kanji. Combined with our smart learning algorithm and the vocabulary you pick up along the way, it turns kanji study into real reading ability." } },
                            { "@type": "Question", "name": "What is Bring Your Own Manga?", "acceptedAnswer": { "@type": "Answer", "text": "Bring Your Own Manga lets you upload manga you already own, as image files or a PDF, and read it inside OniKanji. Our reader adds furigana to every kanji automatically and gives you one-tap translations and full grammar breakdowns, so you can read the manga you actually want to read at any level." } },
                            { "@type": "Question", "name": "Do I need to know Japanese to start using OniKanji?", "acceptedAnswer": { "@type": "Answer", "text": "No, OniKanji is designed for beginners through advanced learners, but you do need to know hiragana and katakana before starting. Once you have those basics, you can progress at your own pace through our structured kanji curriculum." } },
                            { "@type": "Question", "name": "How much does OniKanji cost?", "acceptedAnswer": { "@type": "Answer", "text": "The first three levels of OniKanji are completely free, and so are the level-end manga rewards. For the full curriculum and everything beyond level three, check out our subscription plans." } },
                            { "@type": "Question", "name": "How does OniKanji help with JLPT preparation?", "acceptedAnswer": { "@type": "Answer", "text": "Our curriculum covers all kanji required for each JLPT level, from N5 to N1. We're in rapid development with new levels being added regularly, and every kanji and vocabulary word is tagged by JLPT level so you can focus on what your target exam needs." } }
                        ]
                    })}
                </script>
            </Helmet>
            
            <div className="relative z-10">

                <nav className="fixed top-0 left-0 right-0 z-50 bg-white p-2 md:p-4 flex justify-between items-center shadow-md">
                    {/* Logo and Version */}
                    <div className="flex items-center">
                        <img
                            src={logoImage}
                            alt="Onikanji Logo"
                            className="h-10 sm:h-12 md:h-14 lg:h-18 ml-2 md:ml-4 xl:ml-20"
                            width="auto"
                            height="56"
                        />
                    </div>
                    {/* Menu Items */}
                    <div className="hidden lg:flex justify-center items-center space-x-3 xl:space-x-6 mr-2 xl:mr-20">
                        <a
                            href="/pricing"
                            className="text-black text-base xl:text-lg font-semibold whitespace-nowrap hover:text-gray-600 transition duration-300"
                            aria-label="Go to Pricing page"
                        >
                            Pricing
                        </a>
                        <a
                            href="/kanji"
                            className="text-black text-base xl:text-lg font-semibold whitespace-nowrap hover:text-gray-600 transition duration-300"
                            aria-label="Browse all kanji"
                        >
                            Browse Kanji
                        </a>
                        <a
                            href="https://community.onikanji.com/"
                            className="text-black text-base xl:text-lg font-semibold whitespace-nowrap hover:text-gray-600 transition duration-300"
                            aria-label="Visit our community forum"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Community
                        </a>
                        <a
                            href="https://discord.gg/7TEG9cqZTP"
                            className="text-black text-base xl:text-lg font-semibold whitespace-nowrap hover:text-gray-600 transition duration-300"
                            aria-label="Join our Discord"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Discord
                        </a>
                        <a
                            href="#bring-your-own-manga"
                            className="text-black text-base xl:text-lg font-semibold whitespace-nowrap hover:text-gray-600 transition duration-300"
                            aria-label="Learn about Bring Your Own Manga"
                        >
                            Bring Your Own Manga
                        </a>
                        {!loading &&
                            (user ? (
                                <a
                                    href="/home"
                                    className="text-black text-base xl:text-lg font-semibold whitespace-nowrap hover:text-gray-600 transition duration-300"
                                >
                                    Launch App
                                </a>
                            ) : (
                                <a
                                    href="/login"
                                    className="text-black text-base xl:text-lg font-semibold whitespace-nowrap hover:text-gray-600 transition duration-300"
                                >
                                    Sign In
                                </a>
                            ))}
                        <a
                            href="/signup"
                            className="border-2 border-logoColor hover:border-[#015f80] text-white bg-logoColor text-base xl:text-lg font-bold whitespace-nowrap py-2 px-4 rounded-lg hover:bg-[#015f80] transition duration-300"
                        >
                            Get Started
                        </a>
                    </div>
                    {/* Mobile Menu Button */}
                    <button
                        className="lg:hidden text-black text-2xl p-2"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={isMenuOpen}
                    >
                        {isMenuOpen ? <HiX /> : <HiMenu />}
                    </button>
                    {/* Mobile Menu Popup */}
                    {isMenuOpen && (
                        <div
                            ref={menuRef}
                            className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4"
                            role="navigation"
                            aria-label="Mobile navigation menu"
                        >
                            <div className="flex flex-col space-y-4 px-6 text-center">
                                <a
                                    href="/pricing"
                                    className="text-black text-lg font-semibold hover:text-gray-600 transition duration-300"
                                >
                                    Pricing
                                </a>
                                <a
                                    href="/kanji"
                                    className="text-black text-lg font-semibold hover:text-gray-600 transition duration-300"
                                >
                                    Browse Kanji
                                </a>
                                <a
                                    href="https://community.onikanji.com/"
                                    className="text-black text-lg font-semibold hover:text-gray-600 transition duration-300"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Community
                                </a>
                                <a
                                    href="https://discord.gg/7TEG9cqZTP"
                                    className="text-black text-lg font-semibold hover:text-gray-600 transition duration-300"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Discord
                                </a>
                                <a
                                    href="#bring-your-own-manga"
                                    className="text-black text-lg font-semibold hover:text-gray-600 transition duration-300"
                                >
                                    Bring Your Own Manga
                                </a>
                                {!loading && (user ? (
                                    <a
                                        href="/home"
                                        className="text-black text-lg font-semibold hover:text-gray-600 transition duration-300"
                                    >
                                        Launch App
                                    </a>
                                ) : (
                                    <a
                                        href="/login"
                                        className="text-black text-lg font-semibold hover:text-gray-600 transition duration-300"
                                    >
                                        Sign In
                                    </a>
                                ))}
                                <a
                                    href="/signup"
                                    className="border-2 border-logoColor hover:border-[#015f80] text-white bg-logoColor text-lg font-bold py-2 px-4 rounded-lg hover:bg-[#015f80] transition duration-300 text-center"
                                >
                                    Get Started
                                </a>
                            </div>
                        </div>
                    )}
                </nav>

                {/* Full-screen Hero Section */}
                <header className="min-h-screen w-full bg-[#F9F9FB] flex flex-col items-center justify-center relative pt-16 lg:pt-24">
                    <div className="w-full px-4">
                    </div>
                    <div className="container mx-auto px-4 flex-1 flex items-center">
                        <div className="flex flex-col lg:flex-row items-center justify-center w-full py-4 lg:py-8">
                            {/* Right side - Mikan-chan graphic */}
                            <div className="lg:w-1/2 flex justify-center items-center order-1 lg:order-2">
                                <img
                                    src={mikanChanFloatingMainGraphic}
                                    alt="Mikan-chan, the friendly OniKanji mascot"
                                    className="w-auto max-w-full h-auto max-h-[30vh] md:max-h-[36vh] lg:max-h-[60vh] animate-float hover:scale-105 transition-transform duration-300"
                                    width="600"
                                    height="600"
                                    style={{
                                        animation: `float 3s ease-in-out infinite`,
                                        '@keyframes float': {
                                            '0%, 100%': { transform: 'translateY(0)' },
                                            '50%': { transform: 'translateY(-20px)' },
                                        },
                                    }}
                                />
                            </div>

                            {/* Left side - Text content */}
                            <div className="lg:w-1/2 text-center lg:text-left mb-4 lg:mb-0 order-2 lg:order-1 px-4 lg:px-12">
                                <h1 className="text-4xl md:text-6xl font-bold mb-4 md:mb-6 text-logoColor leading-tight">
                                    Learn Kanji Like a Native:
                                    <span className="block mt-2">In-context</span>
                                </h1>
                                <p className="text-xl md:text-2xl lg:text-3xl text-gray-700 mb-8 md:mb-12 font-light">
                                    Master kanji with structured, level-based, context-first lessons. Then immerse yourself with output games, manga, and more.
                                </p>
                                <div className="flex flex-col sm:flex-row sm:justify-center lg:justify-start gap-4 max-w-md mx-auto lg:mx-0">
                                    <a
                                        href="/signup"
                                        className="group relative inline-block bg-[#FF9541] text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 hover:bg-[#E17F2F] shadow-sm hover:shadow-md"
                                        aria-label="Get Started with OniKanji"
                                    >
                                        <span className="relative z-10">Get Started</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Chevron - Hidden below lg breakpoint */}
                    <div className="absolute bottom-8 w-full hidden lg:flex justify-center">
                        <BsChevronDown
                            className="text-logoColor text-4xl animate-bounce cursor-pointer"
                            onClick={() => window.scrollTo({
                                top: window.innerHeight,
                                behavior: 'smooth'
                            })}
                            aria-hidden="true"
                        />
                    </div>
                </header>

                {/* What You Will Learn Section */}
                <div className="w-full py-12 md:py-16 bg-white">
                    <div className="max-w-6xl mx-auto px-4">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-logoColor mb-3 text-center">
                            The OniKanji Experience
                        </h2>
                        <p className="text-xl text-gray-600 mb-12 text-center">
                            Our native-built, structured approach puts kanji in context—exactly how your brain learns best.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {/* Joyo Kanji Completion */}
                            <div className="flex flex-col items-center text-center h-full">
                                <div className="w-24 h-24 mb-6 flex items-center justify-center">
                                    <img src={joyoKanjiIcon} alt="Joyo Kanji Completion" className="max-h-full" />
                                </div>
                                <h3 className="text-xl md:text-2xl font-bold text-logoColor mb-3">
                                    20 Kanji per Level
                                </h3>
                                <p className="text-gray-700">
                                    Progress through carefully structured levels, N5 to N1, with 20 new kanji in each.
                                </p>
                            </div>

                            {/* Accurate Readings */}
                            <div className="flex flex-col items-center text-center h-full">
                                <div className="w-24 h-24 mb-6 flex items-center justify-center">
                                    <img src={bookIcon} alt="Accurate Readings & Context" className="max-h-full" />
                                </div>
                                <h3 className="text-xl md:text-2xl font-bold text-logoColor mb-3">
                                    Accurate Readings & Context
                                </h3>
                                <p className="text-gray-700">
                                    Master both the most relevant on'yomi and kun'yomi for each kanji with multiple context-rich examples.
                                </p>
                            </div>

                            {/* High-Speed Vocabulary */}
                            <div className="flex flex-col items-center text-center h-full">
                                <div className="w-24 h-24 mb-6 flex items-center justify-center">
                                    <img src={nihonIcon} alt="High-Speed Vocabulary" className="max-h-full" />
                            </div>
                                <h3 className="text-xl md:text-2xl font-bold text-logoColor mb-3">
                                    Rapid Vocabulary
                                </h3>
                                <p className="text-gray-700">
                                    Build your Japanese lexicon with 60+ high-frequency vocabulary words in every level—vocabulary that you can actually use.
                                </p>
                        </div>

                            {/* Bring Your Own Manga */}
                            <div className="flex flex-col items-center text-center h-full">
                                <div className="w-24 h-24 mb-6 flex items-center justify-center">
                                <img src={mangaIcon} alt="Bring Your Own Manga" className="max-h-full" />
                                </div>
                                <h3 className="text-xl md:text-2xl font-bold text-logoColor mb-3">
                                    Read What You Own
                                </h3>
                                <p className="text-gray-700">
                                    Upload the manga you already own and read it with automatic furigana, one-tap translations, and grammar breakdowns.
                                </p>
                            </div>
                        </div>
                            </div>
                        </div>

                {/* Android App Section */}
                <div className="w-full py-4 md:py-6 px-4 md:px-8 flex justify-center">
                    <div className="w-full max-w-[90%] max-sm:max-w-full rounded-xl overflow-hidden relative" style={{ backgroundColor: '#004155' }}>
                        <div className="absolute inset-0" style={{
                            backgroundImage: `url(${backgroundTexture})`,
                            backgroundRepeat: 'repeat',
                            opacity: 0.2
                        }}></div>

                        <div className="relative z-10 flex flex-col lg:flex-row items-center lg:items-stretch">
                            <div className="lg:w-3/5 text-white text-center lg:text-left p-6 md:p-10 lg:self-center">
                                <p className="text-sm font-bold uppercase tracking-wide text-[#FF9541] mb-2">New: OniKanji for Android</p>
                                <h2 className="text-3xl md:text-4xl xl:text-5xl font-bold mb-3 text-balance">
                                    Master Kanji, from your pocket
                                </h2>
                                <p className="text-base md:text-xl mb-6 text-gray-100">
                                    Everything from the web app, now available on your phone.
                                </p>
                                <ul className="space-y-3 mb-8 text-left inline-block lg:block text-base md:text-lg">
                                    {[
                                        'Review reminders the moment your kanji are ready',
                                        'Quick 5: five reviews in about a minute, anywhere',
                                        'Type readings in romaji, no Japanese keyboard needed',
                                        'Read your own manga using our assisted tools',
                                    ].map((item) => (
                                        <li key={item} className="flex items-start gap-3">
                                            <span className="mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#FF9541]" aria-hidden="true"></span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 justify-center lg:justify-start">
                                    <a
                                        href={playStoreUrl('android_section')}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Get OniKanji on Google Play"
                                    >
                                        <img src={googlePlayBadge} alt="Get it on Google Play" className="h-16 md:h-[72px] w-auto" width="646" height="250" />
                                    </a>
                                    <span className="inline-block rounded-full border border-white/40 px-4 py-2 text-sm font-medium text-gray-100">
                                        iPhone coming soon
                                    </span>
                                </div>
                            </div>

                            {/* Phone rises from the panel's bottom edge */}
                            <div className="lg:w-2/5 flex justify-center items-end px-6 pt-2 lg:pt-10">
                                <img
                                    src={androidDashboardPhone}
                                    alt="The OniKanji Android app dashboard: 36 reviews are ready"
                                    className="block w-[240px] sm:w-[280px] lg:w-[320px] h-auto"
                                    width="560"
                                    height="1005"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* How You Will Learn Section */}
                <div id="onikanji-method" className="w-full py-16 md:py-24 px-4 md:px-8 bg-white">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12 md:mb-16">
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#004155] mb-4">
                                The OniKanji Method
                        </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Unlock kanji mastery with our integrated approach: context-rich lessons, smart algorithms, personalized progress tracking, and authentic immersive materials.
                            </p>
                        </div>

                        {/* New Lessons Panel */}
                        <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mb-8 shadow-sm flex flex-col lg:flex-row items-center lg:items-stretch gap-6 lg:gap-8">
                            <div className="w-full lg:w-1/2 mb-6 lg:mb-0">
                                {/* Image with tabs */}
                                <div className="relative">
                                    <div className="overflow-hidden rounded-lg shadow-md transition-all duration-300 hover:scale-150">
                                        {activeNewLessonsImage === 1 ? (
                                            <img 
                                                src={newLessonsImage} 
                                                alt="OniKanji New Lessons Interface" 
                                                className="w-full h-auto"
                                            />
                                        ) : (
                                            <img 
                                                src={learnGraphicImage} 
                                                alt="OniKanji Learn Experience" 
                                                className="w-full h-auto"
                                            />
                                        )}
                        </div>
                                    
                                    {/* Image toggle tabs - moved further down */}
                                    <div className="flex justify-center mt-6 md:mt-8 pb-2">
                                        <button 
                                            className={`w-3 h-3 mx-2 rounded-full transition-colors duration-200 ${activeNewLessonsImage === 1 ? 'bg-[#004155]' : 'bg-gray-300 hover:bg-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#004155] focus:ring-opacity-50`}
                                            onClick={() => setActiveNewLessonsImage(1)}
                                            aria-label="Show first image"
                                        ></button>
                                        <button 
                                            className={`w-3 h-3 mx-2 rounded-full transition-colors duration-200 ${activeNewLessonsImage === 2 ? 'bg-[#004155]' : 'bg-gray-300 hover:bg-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#004155] focus:ring-opacity-50`}
                                            onClick={() => setActiveNewLessonsImage(2)}
                                            aria-label="Show second image"
                                        ></button>
                    </div>
                </div>
                            </div>
                            <div className="w-full lg:w-1/2 flex flex-col justify-center items-center text-center lg:text-left lg:items-start">
                                <h3 className="text-2xl md:text-3xl font-bold text-[#004155] mb-4">
                                    New Lessons
                                </h3>
                                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                                    Learn new kanji and vocabulary through structured micro lessons that build your foundation. Each lesson introduces curated sets of words with example sentences and context-first exposure.
                                </p>
                            </div>
                        </div>

                        {/* SRS Reviews Panel */}
                        <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mb-8 shadow-sm flex flex-col lg:flex-row items-center lg:items-stretch gap-6 lg:gap-8">
                            <div className="w-full lg:w-1/2 mb-6 lg:mb-0 order-1 lg:order-2">
                                {/* Image with tabs */}
                                <div className="relative">
                                    <div className="overflow-hidden rounded-lg shadow-md transition-all duration-300 hover:scale-150">
                                        {activeSRSReviewsImage === 1 ? (
                                            <img
                                                src={srsReviewImage1}
                                                alt="OniKanji SRS Reviews Interface"
                                                className="w-full h-auto"
                                            />
                                        ) : activeSRSReviewsImage === 2 ? (
                                            <img
                                                src={srsReviewImage2}
                                                alt="OniKanji SRS Reviews Experience"
                                                className="w-full h-auto"
                                            />
                                        ) : activeSRSReviewsImage === 3 ? (
                                            <img
                                                src={statsTrackingImage}
                                                alt="OniKanji Stats Tracking"
                                                className="w-full h-auto"
                                            />
                                        ) : (
                                            <img
                                                src={myStatsImage}
                                                alt="OniKanji My Stats Dashboard"
                                                className="w-full h-auto"
                                            />
                                        )}
                            </div>
                                    
                                    {/* Image toggle tabs - moved further down */}
                                    <div className="flex justify-center mt-6 md:mt-8 pb-2 flex-wrap gap-2">
                                        <button
                                            className={`w-3 h-3 mx-1 rounded-full transition-colors duration-200 ${activeSRSReviewsImage === 1 ? 'bg-[#004155]' : 'bg-gray-300 hover:bg-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#004155] focus:ring-opacity-50`}
                                            onClick={() => setActiveSRSReviewsImage(1)}
                                            aria-label="Show first SRS review image"
                                        ></button>
                                        <button
                                            className={`w-3 h-3 mx-1 rounded-full transition-colors duration-200 ${activeSRSReviewsImage === 2 ? 'bg-[#004155]' : 'bg-gray-300 hover:bg-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#004155] focus:ring-opacity-50`}
                                            onClick={() => setActiveSRSReviewsImage(2)}
                                            aria-label="Show second SRS review image"
                                        ></button>
                                        <button
                                            className={`w-3 h-3 mx-1 rounded-full transition-colors duration-200 ${activeSRSReviewsImage === 3 ? 'bg-[#004155]' : 'bg-gray-300 hover:bg-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#004155] focus:ring-opacity-50`}
                                            onClick={() => setActiveSRSReviewsImage(3)}
                                            aria-label="Show stats tracking image"
                                        ></button>
                                        <button
                                            className={`w-3 h-3 mx-1 rounded-full transition-colors duration-200 ${activeSRSReviewsImage === 4 ? 'bg-[#004155]' : 'bg-gray-300 hover:bg-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#004155] focus:ring-opacity-50`}
                                            onClick={() => setActiveSRSReviewsImage(4)}
                                            aria-label="Show my stats dashboard image"
                                        ></button>
                                </div>
                            </div>
                        </div>
                            <div className="w-full lg:w-1/2 order-2 lg:order-1 flex flex-col justify-center items-center text-center lg:text-left lg:items-start">
                                <h3 className="text-2xl md:text-3xl font-bold text-[#004155] mb-4">
                                    SRS Reviews & Stats
                                </h3>
                                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                                    Master kanji efficiently with our intelligent SRS system. Our advanced algorithms dynamically adjust question types and difficulty levels while keeping your review queue manageable and effective. Monitor your improvement through comprehensive statistics and intuitive visualizations that showcase your Japanese learning progress over time.
                                </p>
                            </div>
                                </div>

                        {/* Level Up Panel */}
                        <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mb-8 shadow-sm flex flex-col lg:flex-row items-center lg:items-stretch gap-6 lg:gap-8">
                            <div className="w-full lg:w-1/2 mb-6 lg:mb-0">
                                {/* Image with tabs */}
                                <div className="relative">
                                    <div className="overflow-hidden rounded-lg shadow-md transition-all duration-300 hover:scale-150">
                                        {activeLevelUpImage === 1 ? (
                                            <img 
                                                src={leaderboardImage} 
                                                alt="OniKanji leaderboard: most reviews, accuracy, speed run, streak and Output Battle boards" 
                                                className="w-full h-auto"
                                            />
                                        ) : (
                                            <img 
                                                src={levelUpImage} 
                                                alt="OniKanji Level Up" 
                                                className="w-full h-auto"
                                            />
                                        )}
                                    </div>
                                    
                                    {/* Image toggle tabs - moved further down */}
                                    <div className="flex justify-center mt-6 md:mt-8 pb-2">
                                        <button 
                                            className={`w-3 h-3 mx-2 rounded-full transition-colors duration-200 ${activeLevelUpImage === 1 ? 'bg-[#004155]' : 'bg-gray-300 hover:bg-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#004155] focus:ring-opacity-50`}
                                            onClick={() => setActiveLevelUpImage(1)}
                                            aria-label="Show leaderboard image"
                                        ></button>
                                        <button 
                                            className={`w-3 h-3 mx-2 rounded-full transition-colors duration-200 ${activeLevelUpImage === 2 ? 'bg-[#004155]' : 'bg-gray-300 hover:bg-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#004155] focus:ring-opacity-50`}
                                            onClick={() => setActiveLevelUpImage(2)}
                                            aria-label="Show level up image"
                                        ></button>
                                    </div>
                                </div>
                            </div>
                            <div className="w-full lg:w-1/2 flex flex-col justify-center items-center text-center lg:text-left lg:items-start">
                                <h3 className="text-2xl md:text-3xl font-bold text-[#004155] mb-4">
                                    Level Up & Compete
                                </h3>
                                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                                    Earn experience by completing lessons and reviews. Level up, unlock the next chapter of our original manga, and see how you stack up on the leaderboards.
                                </p>
                            </div>
                        </div>

                            </div>
                </div>

                <div className="px-4 pb-12 md:px-8" id="output-battle">
                    <OutputBattleTeaser signupRequired={!user} />
                </div>

                {/* Bring Your Own Manga Section */}
                <div id="bring-your-own-manga" className="w-full py-16 md:py-24 px-4 md:px-8 bg-[#F9F9FB]">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex flex-col items-center">
                            {/* Header */}
                            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
                                <span className="inline-block bg-[#FF9541] text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-4">
                                    New
                                </span>
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#004155] mb-4">
                                    Bring Your Own Manga
                                </h2>
                                <p className="text-lg md:text-xl text-gray-600">
                                    OniKanji turns your favorite manga into seamless study sessions. Upload the manga you own and read it with one-tap translations, automatic furigana, and full grammar breakdowns.
                                </p>
                            </div>

                            {/* Asset - full width, scales with viewport */}
                            <img
                                src={uploadYourMangaImage}
                                alt="OniKanji reader showing a user-uploaded manga page with one-tap translation, furigana, and a full grammar breakdown"
                                className="w-full h-auto mb-10 md:mb-12 transition-transform duration-300 hover:scale-[1.02]"
                            />

                            {/* Feature bullets */}
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 w-full max-w-3xl mx-auto mb-10 text-left">
                                {[
                                    { strong: 'Bring manga you own', rest: '—upload your purchased manga or PDFs' },
                                    { strong: 'Built-in OCR', rest: '—no clunky browser extensions or extra software to wrangle' },
                                    { strong: 'One-click translations', rest: 'to English' },
                                    { strong: 'One-click grammar & context breakdowns', rest: '' },
                                    { strong: 'Furigana added automatically', rest: '' },
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start">
                                        <svg className="w-6 h-6 text-[#FF9541] flex-shrink-0 mr-3 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="text-base md:text-lg text-gray-700">
                                            <strong className="text-[#004155] font-semibold">{item.strong}</strong>
                                            {item.rest && ` ${item.rest}`}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            {/* CTA */}
                            <a
                                href="/immersion-hub/upload"
                                className="inline-block bg-[#FF9541] hover:bg-[#E17F2F] text-white font-bold py-3 px-8 rounded-lg transition duration-300 shadow-md hover:shadow-lg"
                            >
                                Upload Your First Manga
                            </a>
                        </div>
                    </div>
                </div>

                {/* Testimonials Section */}
                <div className="w-full py-16 md:py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-4">
                        <div className="text-center mb-12 md:mb-16">
                            <p className="text-sm md:text-base font-bold uppercase tracking-wide text-[#FF9541] mb-3">What our users say</p>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#004155] mb-4 text-balance">
                                340,000+ kanji, vocab and sentence reviews completed by our learners
                            </h2>
                            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
                                Join an active community of satisfied learners who have transformed their kanji journey with OniKanji.
                            </p>
                        </div>

                        <div className="flex flex-col lg:flex-row gap-8 items-start">
                            {/* Left side - Video */}
                            {isLargeScreen && (
                                <div className="w-full lg:w-2/5">
                                    <div className="relative w-full rounded-lg overflow-hidden shadow-lg">
                                        <video 
                                            className="w-full h-auto"
                                            autoPlay 
                                            loop 
                                            muted 
                                            playsInline
                                        >
                                            <source src={craneGameVideo} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                </div>
                            )}

                            {/* Right side - Testimonial Cards */}
                            <div className={`w-full ${isLargeScreen ? 'lg:w-3/5' : ''} space-y-6`}>
                                {/* Testimonial 1 */}
                                <div className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                                    <div className="flex items-start mb-4">
                                        <div className="text-4xl text-logoColor opacity-50 mr-4 leading-none">"</div>
                                        <p className="text-gray-700 text-base lg:text-lg flex-1">
                                            Onikanji's learning in context, makes vocabulary a first class citizen with each Kanji you learn, as well as a variety of tools to understand and deepen your study, makes this the most effective tool to learn Kanji and Japanese vocabulary I've found.
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-semibold text-logoColor">- Joao P., N5 Level Student</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs font-bold py-1 px-2 rounded-md flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                                </svg>
                                                <span>Rank 4 High Scores</span>
                                            </div>
                                            <span className="text-xs text-gray-500 flex items-center">
                                                <svg className="w-3 h-3 mr-1 text-[#5865F2]" fill="currentColor" viewBox="0 0 71 55" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.4349C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.4591 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                                                </svg>
                                                Discord Member
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Testimonial 3 */}
                                <div className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                                    <div className="flex items-start mb-4">
                                        <div className="text-4xl text-logoColor opacity-50 mr-4 leading-none">"</div>
                                        <p className="text-gray-700 text-base lg:text-lg flex-1">
                                            What I like the most about OniKanji is that I'm not only learning Kanji, but also useful vocabulary.
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-semibold text-logoColor">- Klaas D., N5 Level Student</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs font-bold py-1 px-2 rounded-md flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                                </svg>
                                                <span>Rank 3 High Scores</span>
                                            </div>
                                            <span className="text-xs text-gray-500 flex items-center">
                                                <svg className="w-3 h-3 mr-1 text-[#5865F2]" fill="currentColor" viewBox="0 0 71 55" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.4349C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.4591 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                                                </svg>
                                                Discord Member
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Testimonial 4 */}
                                <div className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                                    <div className="flex items-start mb-4">
                                        <div className="text-4xl text-logoColor opacity-50 mr-4 leading-none">"</div>
                                        <p className="text-gray-700 text-base lg:text-lg flex-1">
                                            With OniKanji, learning vocabulary alongside each kanji has really helped solidify the meanings and readings in my head, such that I'm seeing noticeable differences in my reading speed and comprehension. Running into vocabulary learned through OniKanji while reading is very rewarding. That combined with the immersion features makes this an app I use daily for my Japanese studies.
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-semibold text-logoColor">- Christina C., JLPT N3</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-bold py-1 px-2 rounded-md flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                                </svg>
                                                <span>#1 High Scores</span>
                                            </div>
                                            <span className="text-xs text-gray-500 flex items-center">
                                                <svg className="w-3 h-3 mr-1 text-[#5865F2]" fill="currentColor" viewBox="0 0 71 55" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.4349C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.4591 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                                                </svg>
                                                Discord Member
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-12">
                            <a 
                                href="https://discord.gg/7TEG9cqZTP" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="inline-block bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold text-base md:text-lg py-2 px-4 md:px-6 lg:px-8 rounded-lg transition duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                            >
                                Join the Discord Community
                            </a>
                        </div>
                    </div>
                </div>

                {/* Godzilla Section */}
                <div className="w-full py-8 md:py-16 relative flex justify-center items-center">
                    <div className="relative w-full md:w-4/5 lg:w-4/5 xl:w-4/5 border-4 border-logoColor overflow-hidden rounded-lg">
                        <picture>
                            <source
                                media="(max-width: 768px)"
                                srcSet={godzillaJoinUsImageMobile}
                            />
                            <source
                                media="(min-width: 769px)"
                                srcSet={godzillaJoinUsImage}
                            />
                            <img
                                src={godzillaJoinUsImage} // Fallback image
                                alt="Join Us Godzilla"
                                className="w-full h-auto object-contain"
                            />
                        </picture>
                        <div className="absolute inset-0 flex items-center p-4 md:p-8">
                            <div className="w-full md:w-1/2 text-left bg-logoColor bg-opacity-95 p-6 rounded-lg">
                                <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 md:mb-4 leading-tight">
                                    Your First Three Levels Are Free
                                </h2>
                                <p className="text-lg md:text-xl lg:text-2xl xl:text-3xl text-white mb-4 md:mb-6 leading-relaxed">
                                    Start learning kanji today. No credit card needed.
                                </p>
                                <a
                                    href="/signup"
                                    className="inline-block bg-[#FF9541] hover:bg-[#E17F2F] text-white font-bold py-3 px-6 rounded-lg transition duration-300 shadow-md hover:shadow-lg"
                                >
                                    Start Free Now
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="w-full py-16 md:py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-4">
                        <div className="flex flex-col lg:flex-row gap-8 items-center">
                            {/* Left column - Image and title */}
                            <div className="w-full lg:w-2/5 mb-8 lg:mb-0 flex flex-col items-center lg:items-start">
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-logoColor mb-6 text-center lg:text-left">Frequently asked questions</h2>
                                <img src={faqImage} alt="Frequently Asked Questions" className="w-full h-auto object-contain mt-4" />
                                <div className="mt-6">
                                    <a href="https://community.onikanji.com/t/contact-us" className="flex items-center text-logoColor hover:text-[#015f80] transition duration-300">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4-4-4z" />
                                        </svg>
                                        <span className="text-lg font-medium">Still need help? Chat to us</span>
                                    </a>
                                </div>
                            </div>
                            {/* Right column - FAQ items */}
                            <div className="w-full lg:w-3/5 flex items-center">
                                <div className="space-y-6 flex flex-col justify-center w-full">
                                    {/* FAQ Item 1 */}
                                    <div className="border-b border-gray-200 pb-6">
                                        <button onClick={() => toggleFaq(0)} className="w-full flex justify-between items-center text-left">
                                            <span className="text-lg font-semibold text-[#004155]">What makes OniKanji different from other Japanese learning apps?</span>
                                            <svg className={`w-6 h-6 flex-shrink-0 text-logoColor transition-transform ${openFaqIndex === 0 ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                                        </button>
                                        {openFaqIndex === 0 && (
                                            <div className="mt-4 text-gray-600 text-left">
                                                OniKanji focuses on a context-first approach to learning kanji. Each review question is paired with a relevant context sentence that serves as a natural mnemonic to help you remember the kanji. Combined with our smart learning algorithm and the vocabulary you pick up along the way, it turns kanji study into real reading ability.
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* FAQ Item 2 */}
                                    <div className="border-b border-gray-200 pb-6">
                                        <button onClick={() => toggleFaq(1)} className="w-full flex justify-between items-center text-left">
                                            <span className="text-lg font-semibold text-[#004155]">What is Bring Your Own Manga?</span>
                                            <svg className={`w-6 h-6 flex-shrink-0 text-logoColor transition-transform ${openFaqIndex === 1 ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                                        </button>
                                        {openFaqIndex === 1 && (
                                            <div className="mt-4 text-gray-600 text-left">
                                                Bring Your Own Manga lets you upload manga you already own, as image files or a PDF, and read it inside OniKanji. Our reader adds furigana to every kanji automatically and gives you one-tap translations and full grammar breakdowns, so you can read the manga you actually want to read at any level.
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* FAQ Item 3 */}
                                    <div className="border-b border-gray-200 pb-6">
                                        <button onClick={() => toggleFaq(2)} className="w-full flex justify-between items-center text-left">
                                            <span className="text-lg font-semibold text-[#004155]">Do I need to know Japanese to start using OniKanji?</span>
                                            <svg className={`w-6 h-6 flex-shrink-0 text-logoColor transition-transform ${openFaqIndex === 2 ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                                        </button>
                                        {openFaqIndex === 2 && (
                                            <div className="mt-4 text-gray-600 text-left">
                                                No, OniKanji is designed for beginners through advanced learners, but you do need to know hiragana and katakana before starting. Once you have those basics, you can progress at your own pace through our structured kanji curriculum.
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* FAQ Item 4 */}
                                    <div className="border-b border-gray-200 pb-6">
                                        <button onClick={() => toggleFaq(3)} className="w-full flex justify-between items-center text-left">
                                            <span className="text-lg font-semibold text-[#004155]">How Much Does It Cost?</span>
                                            <svg className={`w-6 h-6 flex-shrink-0 text-logoColor transition-transform ${openFaqIndex === 3 ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                                        </button>
                                        {openFaqIndex === 3 && (
                                            <div className="mt-4 text-gray-600 text-left">
                                                The first three levels of OniKanji are completely free, and so are the level-end manga rewards. For the full curriculum and everything beyond level three, check out our <a href="/pricing" className="text-logoColor hover:underline">subscription plans</a>.
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* FAQ Item 5 */}
                                    <div className="border-b border-gray-200 pb-6">
                                        <button onClick={() => toggleFaq(4)} className="w-full flex justify-between items-center text-left">
                                            <span className="text-lg font-semibold text-[#004155]">How does OniKanji help with JLPT preparation?</span>
                                            <svg className={`w-6 h-6 flex-shrink-0 text-logoColor transition-transform ${openFaqIndex === 4 ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                                        </button>
                                        {openFaqIndex === 4 && (
                                            <div className="mt-4 text-gray-600 text-left">
                                                Our curriculum covers all kanji required for each JLPT level, from N5 to N1. We're in rapid development with new levels being added regularly, and every kanji and vocabulary word is tagged by JLPT level so you can focus on what your target exam needs.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            <Footer />

        </div>
    );
};

export default OniKanjiLandingPage;
